/**
 * GucResultsPanel — the LEAN surface. This is the point of v2.
 *
 * The Sizer Lab is a testing instrument: it itemizes every reduction, citation,
 * provenance chip and advisory because someone is auditing the engine. v2 is for
 * someone pricing a deal, so the page shows the five things that decide whether
 * to keep going and folds everything else into one Details section. What was cut
 * is not lost — the Explain-this-result dialog IS the verbose view, which is why
 * the page can stay this short.
 *
 * Order, per spec §3:
 *   2. one verdict line
 *   3. cures, when anything fails — buttons, not prose
 *   4. leverage table, reductions as reason + amount (citations behind an expander)
 *   5. required liquidity as arithmetic
 *   6. everything else, collapsed
 *
 * RENDER-ONLY. Every figure below is a field of `POST /pricing/scenario/rtl`.
 * The one exception is the budget-share cure, which turns the engine's own
 * `maxLoanAtMax` back into the percentage the FORM stores — a form input, not an
 * answer, and it is re-priced before it means anything.
 */
import { useState } from "react";

import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { SZ, ResultStack } from "../../sizersShared/sizerUi";
import {
  fmtMoney,
  fmtPct,
  fmtAdjPct,
  fmtRate,
  fmtRateDec,
  humanize,
  resolvedSource,
} from "../../sizersShared/sizerFormat";
import { Callout, GuidelineCheckList } from "../../sizersShared/sizerResultUi";
import { isInfoFinding } from "../gucConstants";
import { verdict } from "../gucPlainSummary";
import { rtlBuyRate, rtlRequiredCoupon } from "../../sizersShared/rtlRateDisplay";

/* ------------------------------------------------------------------ atoms */

const Card = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: SZ.card,
      border: `1px solid ${SZ.line}`,
      borderRadius: SZ.radius,
      boxShadow: SZ.shadow,
      p: "15px 17px",
      ...sx,
    }}
  >
    {children}
  </Box>
);

const CardTitle = ({ children, right }) => (
  <Flx jb ac g={1} sx={{ mb: 1.4 }}>
    <Txt
      sx={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: ".08em",
        textTransform: "uppercase",
        color: SZ.ink400,
      }}
    >
      {children}
    </Txt>
    {right}
  </Flx>
);

const Mono = ({ children, sx }) => (
  <Box
    component="span"
    sx={{ fontFamily: SZ.mono, fontVariantNumeric: "tabular-nums", ...sx }}
  >
    {children}
  </Box>
);

const Row = ({ k, v, src, tone, last }) => (
  <Flx
    jb
    g={1}
    sx={{
      alignItems: "baseline",
      py: "6px",
      borderBottom: last ? "none" : `1px dashed ${SZ.line}`,
    }}
  >
    <Txt sx={{ fontSize: 12.5, color: SZ.ink500 }}>{k}</Txt>
    <Box sx={{ textAlign: "right" }}>
      <Mono
        sx={{
          fontSize: 12.5,
          fontWeight: tone ? 600 : 400,
          color: tone === "pos" ? SZ.good : tone === "neg" ? SZ.bad : SZ.ink900,
        }}
      >
        {v}
      </Mono>
      {src && (
        <Txt component="span" sx={{ fontSize: 10, color: SZ.ink400, ml: 0.75 }}>
          {src}
        </Txt>
      )}
    </Box>
  </Flx>
);

/** A cure. A button, not a sentence — spec §3.3. */
const CureButton = ({ primary, title, sub, onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      display: "block",
      width: "100%",
      textAlign: "left",
      border: `1px solid ${primary ? SZ.teal600 : SZ.lineStrong}`,
      background: primary ? SZ.teal50 : SZ.card,
      borderRadius: SZ.radiusSm,
      p: "10px 13px",
      cursor: "pointer",
      fontFamily: "inherit",
      transition: "all .12s",
      "&:hover": { borderColor: SZ.teal500, background: SZ.teal50 },
    }}
  >
    <Txt
      component="div"
      sx={{
        fontSize: 12.5,
        fontWeight: 600,
        color: primary ? SZ.teal700 : SZ.ink900,
      }}
    >
      {title}
    </Txt>
    {sub && (
      <Txt component="div" sx={{ fontSize: 11, color: SZ.ink500, mt: "2px" }}>
        {sub}
      </Txt>
    )}
  </Box>
);

/** A disclosure, used for the citations expander and for Details. */
const Disclosure = ({ label, count, children, sx }) => {
  const [open, setOpen] = useState(false);
  return (
    <Box sx={sx}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          border: 0,
          background: "transparent",
          p: 0,
          fontFamily: "inherit",
          fontSize: 11.5,
          fontWeight: 600,
          color: SZ.teal700,
          cursor: "pointer",
          "&:hover": { textDecoration: "underline" },
        }}
      >
        {open ? "Hide" : "Show"} {label}
        {count != null ? ` (${count})` : ""}
      </Box>
      {open && <Box sx={{ mt: 1 }}>{children}</Box>}
    </Box>
  );
};

/* ------------------------------------------------------------------ blocks */

/** 2. ONE verdict line. */
const Verdict = ({ result }) => {
  const v = verdict(result);
  if (!v) return null;
  const good = v.tone === "good";
  return (
    <Card
      sx={{
        borderColor: good ? `${SZ.good}44` : `${SZ.bad}44`,
        background: good ? SZ.goodBg : SZ.badBg,
      }}
    >
      <Flx g={1} sx={{ alignItems: "flex-start" }}>
        {good ? (
          <CheckCircleRoundedIcon sx={{ color: SZ.good, fontSize: 18, mt: "1px" }} />
        ) : (
          <ErrorOutlineRoundedIcon sx={{ color: SZ.bad, fontSize: 18, mt: "1px" }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Txt
            component="div"
            sx={{
              fontSize: 13.5,
              fontWeight: 700,
              color: good ? SZ.good : SZ.bad,
            }}
          >
            {v.headline}
          </Txt>
          {v.lines.map((line, i) => (
            <Txt
              key={i}
              component="div"
              sx={{ fontSize: 12.5, color: SZ.ink700, mt: "4px" }}
            >
              {line}
            </Txt>
          ))}
        </Box>
      </Flx>
    </Card>
  );
};

/**
 * 3. CURES. The at-cap reprice first, because it is the one that clears it;
 * then the steps below it; then the budget-share lever with the borrower-cash
 * figure it implies.
 *
 * `suggestions[]` carries both shapes the engine emits: per-over-metric rows
 * (`metric`, `baseLoanAmountAtMax`, `maxLoanAtMax`) and up to two measured
 * alternatives (`type`, `baseLoanAmount`, `passing`). The binding amount is the
 * smallest advance that satisfies every breached metric — that is what "clears
 * it" means, and it is the engine's own `min`, not ours.
 */
const Cures = ({ result, cures, onRepriceAt, onApplyBudgetShare }) => {
  if (!cures || (!cures.binding && !cures.budgetShare)) return null;
  return (
    <Card>
      <CardTitle>How to clear it</CardTitle>
      <Flx column g={1}>
        {cures.binding != null && (
          <CureButton
            primary
            title={`Re-price at ${fmtMoney(cures.binding)} — clears it`}
            sub={
              cures.bindingMetric
                ? `The largest advance that fits ${cures.bindingMetric} at its reduced maximum.`
                : "The largest advance that fits every breached maximum."
            }
            onClick={() => onRepriceAt(cures.binding)}
          />
        )}
        {(cures.steps || []).map((s) => (
          <CureButton
            key={s.label}
            title={`Re-price at ${fmtMoney(s.amount)}`}
            sub={
              s.passing == null
                ? `${s.label} below the cap.`
                : `${s.label} below the cap — priced as ${s.passing ? "passing" : "still over"}.`
            }
            onClick={() => onRepriceAt(s.amount)}
          />
        ))}
        {cures.budgetShare && (
          <CureButton
            title={`Finance ${fmtPct(cures.budgetShare.financedBudgetPercent, 1)} of the budget`}
            sub={`Holdback falls to ${fmtMoney(cures.budgetShare.holdback)}; the borrower brings ${fmtMoney(cures.budgetShare.borrowerCash)} in cash. The advance does not move.`}
            onClick={() => onApplyBudgetShare(cures.budgetShare.financedBudgetPercent)}
          />
        )}
      </Flx>
      <Txt component="div" sx={{ fontSize: 11, color: SZ.ink400, mt: 1.25 }}>
        Each of these writes the form input it names. Re-price to confirm.
      </Txt>
    </Card>
  );
};

/**
 * 4. LEVERAGE — metric / requested / max after reductions / over.
 *
 * The reduction rows below the table say reason and amount only; the citation is
 * behind the expander. A `front_end` row is scoped to LTV and LTC, so it says
 * so — the reason its amount does not appear in `totalLeverageReduction` is that
 * `apply_leverage_reductions` deliberately excludes it from that sum.
 *
 * A fixed comparison of four known metrics, not a data grid — nothing to sort,
 * filter or navigate, so AG Grid has no job here.
 */
const LeverageCard = ({ result }) => {
  const rows = result.leverage || [];
  if (!rows.length) return null;
  const reductions = result.leverageReductions || [];
  const over = rows.filter((r) => r.over).length;

  const th = {
    fontSize: 10.5,
    fontWeight: 600,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    color: SZ.ink400,
    textAlign: "right",
    padding: "0 0 7px",
    borderBottom: `1px solid ${SZ.line}`,
  };
  const td = {
    padding: "7px 0",
    borderBottom: `1px dashed ${SZ.line}`,
    fontFamily: SZ.mono,
    fontVariantNumeric: "tabular-nums",
    textAlign: "right",
    fontSize: 12.5,
  };

  return (
    <Card>
      <CardTitle
        right={
          over > 0 ? (
            <Txt
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".04em",
                textTransform: "uppercase",
                color: SZ.bad,
                background: SZ.badBg,
                borderRadius: "5px",
                p: "2px 8px",
                whiteSpace: "nowrap",
              }}
            >
              {over} over
            </Txt>
          ) : null
        }
      >
        Leverage
      </CardTitle>

      <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
        <Box component="thead">
          <Box component="tr">
            <Box component="th" sx={{ ...th, textAlign: "left" }}>
              Metric
            </Box>
            <Box component="th" sx={th}>
              Requested
            </Box>
            <Box component="th" sx={th}>
              Max
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {rows.map((r, i) => (
            <Box component="tr" key={r.metric || i}>
              <Box
                component="td"
                sx={{
                  ...td,
                  fontFamily: "inherit",
                  textAlign: "left",
                  fontWeight: 500,
                  color: SZ.ink500,
                }}
              >
                {r.metric}
              </Box>
              <Box component="td" sx={{ ...td, color: r.over ? SZ.bad : SZ.ink900 }}>
                {fmtPct(r.requested, 2)}
                {r.over && (
                  <Box
                    component="span"
                    sx={{
                      fontFamily: "inherit",
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: SZ.bad,
                      background: SZ.badBg,
                      borderRadius: "4px",
                      p: "1px 5px",
                      ml: 0.75,
                    }}
                  >
                    Over
                  </Box>
                )}
              </Box>
              <Box component="td" sx={{ ...td, color: r.over ? SZ.bad : SZ.ink900 }}>
                {r.maximum == null ? "n/a" : fmtPct(r.maximum, 2)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>

      {reductions.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          <Txt
            component="div"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: SZ.ink400,
              mb: 0.5,
            }}
          >
            Reductions applied to the max
          </Txt>
          {reductions.map((r, i) => (
            <Flx
              key={r.code || i}
              jb
              g={1}
              sx={{ alignItems: "baseline", py: "4px" }}
            >
              <Txt sx={{ fontSize: 12, color: SZ.ink700, flex: 1, minWidth: 0 }}>
                {r.reason}
                {r.scope === "front_end" && (
                  <Box
                    component="span"
                    sx={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: SZ.warn,
                      background: SZ.warnBg,
                      borderRadius: "4px",
                      p: "1px 5px",
                      ml: 0.75,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Front end — LTV/LTC only
                  </Box>
                )}
              </Txt>
              <Mono sx={{ fontSize: 12, fontWeight: 600, color: SZ.warn }}>
                {fmtPct(r.reduction, 2)}
              </Mono>
            </Flx>
          ))}
          <Disclosure label="citations" count={reductions.length} sx={{ mt: 1 }}>
            {reductions.map((r, i) => (
              <Txt
                key={r.code || i}
                component="div"
                sx={{ fontSize: 11, color: SZ.ink400, py: "2px" }}
              >
                {r.code} — {r.citation || "no citation"}
              </Txt>
            ))}
          </Disclosure>
        </Box>
      )}
    </Card>
  );
};

/**
 * 5. REQUIRED LIQUIDITY AS ARITHMETIC.
 *
 * Not a single total with a shrug — the components the guideline sums, each
 * named, adding to the required figure and set against what resolved. The
 * closing-costs row says when it is an estimate rather than an entered number,
 * because the engine tells us (`closingCostsBasis`) and a $30k estimate the user
 * did not type is worth marking as one.
 */
const LiquidityCard = ({ req }) => {
  if (!req || req.requiredLiquidity == null) return null;
  const c = req.liquidityComponents || {};
  return (
    <Card>
      <CardTitle>Required liquidity</CardTitle>
      <Row
        k={`${req.reserveMonths ?? "—"} months of payments`}
        v={fmtMoney(c.monthsPayments)}
      />
      <Row k="Non-financed construction" v={fmtMoney(c.nonFinancedConstruction)} />
      <Row k="Down payment" v={fmtMoney(c.downPayment)} />
      <Row
        k="Closing costs (borrower cash)"
        v={fmtMoney(c.closingCosts)}
        src={c.closingCostsBasis === "entered" ? "entered" : c.closingCostsBasis}
      />
      {/* §12b: a borrower-paid reserve is a cash use; a financed one is inside
          the loan amount and never shows here. */}
      {Number(c.borrowerFundedInterestReserve) > 0 && (
        <Row
          k="Interest reserve (borrower cash)"
          v={fmtMoney(c.borrowerFundedInterestReserve)}
        />
      )}
      <Flx
        jb
        g={1}
        sx={{
          alignItems: "baseline",
          py: "9px",
          mt: "3px",
          borderTop: `1px solid ${SZ.line}`,
        }}
      >
        <Txt sx={{ fontSize: 13, fontWeight: 700, color: SZ.ink900 }}>Required</Txt>
        <Mono sx={{ fontSize: 14, fontWeight: 700, color: SZ.ink900 }}>
          {fmtMoney(req.requiredLiquidity)}
        </Mono>
      </Flx>
      <Row k="Resolved liquidity" v={fmtMoney(req.liquidityResolved)} />
      <Row
        k="Liquidity Coverage"
        v={
          req.liquidityCovered
            ? "Sufficient Liquidity"
            : `Short ${fmtMoney(req.liquidityShortfall)}`
        }
        tone={req.liquidityCovered ? "pos" : "neg"}
        last
      />
    </Card>
  );
};

/** 6. EVERYTHING ELSE — one collapsed section. The lab shows these expanded. */
const Details = ({ result }) => {
  const [open, setOpen] = useState(false);
  const rate = result.rate || {};
  const resolved = result.resolved || {};
  const cl = resolved.classification || {};
  const takeout = result.guidelineCheck || {};
  const market = result.marketAssessment || {};

  const adjusters = [
    { k: "Term", v: rate.termAdjPct },
    { k: "Class", v: rate.classAdjPct },
    { k: rate.uptickTier ? `Amount ${rate.uptickTier}` : "Amount", v: rate.loanAmountUptickPct },
    {
      k: rate.leverageBand ? `Leverage ${rate.leverageBand}` : "Leverage",
      v: rate.leverageAdjPct,
    },
  ].filter((a) => a.v != null && a.v !== 0);

  return (
    <Box
      sx={{
        bgcolor: SZ.card,
        border: `1px solid ${SZ.line}`,
        borderRadius: SZ.radius,
        boxShadow: SZ.shadow,
        overflow: "hidden",
      }}
    >
      <Flx
        jb
        ac
        onClick={() => setOpen((v) => !v)}
        sx={{
          p: "13px 17px",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { background: "#fbfcfc" },
        }}
      >
        <Txt
          sx={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: ".08em",
            textTransform: "uppercase",
            color: SZ.ink400,
          }}
        >
          Details
        </Txt>
        <Txt sx={{ fontSize: 11.5, fontWeight: 600, color: SZ.teal700 }}>
          {open ? "Hide" : "Show"}
        </Txt>
      </Flx>

      {open && (
        <Box sx={{ p: "0 17px 15px" }}>
          {/* Rate build-up: starting rate → the adjusters that moved → buy rate. */}
          <Txt
            component="div"
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: ".05em",
              textTransform: "uppercase",
              color: SZ.ink400,
              mb: 0.5,
            }}
          >
            Rate build-up
          </Txt>
          {rate.error ? (
            <Callout tone="warn" title="Rate block unavailable">
              {rate.error}
            </Callout>
          ) : (
            <>
              <Row k="Starting rate" v={fmtRate(rate.baseRate12mo)} />
              {adjusters.map((a) => (
                <Row key={a.k} k={a.k} v={fmtAdjPct(a.v)} />
              ))}
              {rate.accountSpread != null && (
                <Row k="Account spread" v={fmtRate(rate.accountSpread)} />
              )}
              <Row k="Required base rate" v={fmtRateDec(rate.requiredBaseRate)} />
              {rtlBuyRate(rate) != null && (
                <Row k="Buy rate" v={fmtRateDec(rtlBuyRate(rate))} />
              )}
              <Row k="Required coupon" v={fmtRateDec(rtlRequiredCoupon(rate))} last />
            </>
          )}

          {/* Class scoring detail. */}
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${SZ.line}` }}>
            <Txt
              component="div"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                color: SZ.ink400,
                mb: 0.5,
              }}
            >
              Borrower class
            </Txt>
            <Row
              k="Class"
              v={resolved.borrowerClass || cl.borrowerClass || "—"}
              tone={
                (resolved.borrowerClass || cl.borrowerClass) === "C" ? "neg" : undefined
              }
            />
            <Row
              k="Resolved FICO"
              v={resolved.fico?.value ?? "—"}
              src={resolvedSource(resolved.fico)}
            />
            <Row
              k="Cumulative experience"
              v={resolved.experience?.value ?? "—"}
              src={resolvedSource(resolved.experience)}
            />
            <Row
              k="Resolved liquidity"
              v={fmtMoney(resolved.liquidity?.value)}
              src={resolvedSource(resolved.liquidity)}
              last
            />
            {result.classExplanation && (
              <Txt
                component="div"
                sx={{ fontSize: 11, color: SZ.ink400, mt: 0.75, fontStyle: "italic" }}
              >
                {result.classExplanation}
              </Txt>
            )}
          </Box>

          {/* Guideline findings, with citations. */}
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${SZ.line}` }}>
            <Txt
              component="div"
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: ".05em",
                textTransform: "uppercase",
                color: SZ.ink400,
                mb: 0.5,
              }}
            >
              Guideline findings
            </Txt>
            <GuidelineCheckList
              exceptions={result.guidelineExceptions}
              facilityHardExceptions={result.facilityHardExceptions}
            />
            {(result.guidelineExceptions || []).some((e) => e.citation) && (
              <Disclosure label="citations" sx={{ mt: 1 }}>
                {(result.guidelineExceptions || [])
                  .filter((e) => e.citation)
                  .map((e, i) => (
                    <Txt
                      key={`${e.code}-${i}`}
                      component="div"
                      sx={{ fontSize: 11, color: SZ.ink400, py: "2px" }}
                    >
                      {e.code} — {e.citation}
                    </Txt>
                  ))}
              </Disclosure>
            )}
          </Box>

          {/* Takeout drawer — the securitization term sheet, not the facility box. */}
          {takeout.takeout && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${SZ.line}` }}>
              <Txt
                component="div"
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  mb: 0.5,
                }}
              >
                Takeout — {humanize(takeout.takeout)}{" "}
                <Box component="span" sx={{ textTransform: "none", fontWeight: 500 }}>
                  ({takeout.mode})
                </Box>
              </Txt>
              {(takeout.takeout_denials || []).length === 0 ? (
                <Txt sx={{ fontSize: 12, color: SZ.ink500 }}>
                  No takeout denials on this scenario.
                </Txt>
              ) : (
                takeout.takeout_denials.map((d, i) => (
                  <Txt
                    key={d.ruleId || i}
                    component="div"
                    sx={{ fontSize: 12, color: SZ.ink700, py: "3px" }}
                  >
                    • {d.reason}
                  </Txt>
                ))
              )}
            </Box>
          )}

          {/* Market chips. */}
          {market.metroStatus === "found" && (
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${SZ.line}` }}>
              <Txt
                component="div"
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: ".05em",
                  textTransform: "uppercase",
                  color: SZ.ink400,
                  mb: 0.5,
                }}
              >
                Market{market.zipCode ? ` — ZIP ${market.zipCode}` : ""}
              </Txt>
              <Row k="Median value (ZHVI)" v={fmtMoney(market.medianValue)} />
              {market.hpaYoY != null && <Row k="HPA YoY" v={fmtPct(market.hpaYoY, 2)} />}
              {market.zillowMultiplier != null && (
                <Row
                  k="ZHVI multiplier"
                  v={`${Number(market.zillowMultiplier).toFixed(2)}×`}
                  last
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

/* ------------------------------------------------------------------ panel */

export default function GucResultsPanel({
  result,
  pending,
  error,
  cures,
  onRepriceAt,
  onApplyBudgetShare,
  // Review mode lays the cards out wide (the verdict across the top, the rest
  // in balanced columns) — the same content, just not a rail.
  masonry,
}) {
  if (error) {
    return (
      <Card>
        <Flx g={1} sx={{ alignItems: "flex-start" }}>
          <ErrorOutlineRoundedIcon sx={{ color: SZ.bad, fontSize: 18 }} />
          <Txt sx={{ fontSize: 13, color: SZ.bad }}>
            {error.message || "Failed to price this loan."}
          </Txt>
        </Flx>
      </Card>
    );
  }

  if (pending && !result) {
    return (
      <Card>
        <Flx center g={1.5} sx={{ py: 4 }}>
          <CircularProgress size={20} sx={{ color: SZ.teal700 }} />
          <Txt sx={{ fontSize: 13, color: SZ.ink500 }}>Pricing…</Txt>
        </Flx>
      </Card>
    );
  }

  if (!result) {
    return (
      <Card sx={{ p: "30px 20px", textAlign: "center" }}>
        <Flx
          center
          sx={{
            width: 40,
            height: 40,
            borderRadius: SZ.radius,
            background: SZ.teal50,
            color: SZ.teal700,
            m: "0 auto 12px",
          }}
        >
          <DescriptionOutlinedIcon sx={{ fontSize: 20 }} />
        </Flx>
        <Txt
          component="div"
          sx={{ fontSize: 13.5, fontWeight: 600, color: SZ.ink900, mb: 0.5 }}
        >
          The answer appears here
        </Txt>
        <Txt
          component="div"
          sx={{ fontSize: 12, color: SZ.ink400, lineHeight: 1.5, maxWidth: 240, mx: "auto" }}
        >
          Fill in the loan, guarantors and property, then price the scenario. The
          amounts above stay live while you type.
        </Txt>
      </Card>
    );
  }

  /* Info findings are notes, and they get one quiet line each — never red, and
     never a Request-exception button. The rest of the findings live in Details. */
  const notes = (result.guidelineExceptions || []).filter(isInfoFinding);

  const cards = (
    <>
      <Cures
        result={result}
        cures={cures}
        onRepriceAt={onRepriceAt}
        onApplyBudgetShare={onApplyBudgetShare}
      />
      <LeverageCard result={result} />
      <LiquidityCard req={result.requirements} />

      {notes.length > 0 && (
        <Card>
          <CardTitle>Notes</CardTitle>
          {notes.map((n, i) => (
            <Flx key={`${n.code}-${i}`} g={0.85} sx={{ alignItems: "flex-start", py: 0.4 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  background: SZ.ink400,
                  mt: "6px",
                  flexShrink: 0,
                }}
              />
              <Txt sx={{ fontSize: 12, color: SZ.ink500 }}>{n.message}</Txt>
            </Flx>
          ))}
        </Card>
      )}

      <Details result={result} />
    </>
  );

  return (
    <Flx column g={1.75}>
      <Verdict result={result} />
      {masonry ? <ResultStack masonry>{cards}</ResultStack> : cards}
    </Flx>
  );
}
